// @flow
import * as React from 'react';
import {Link} from 'docs/components';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {Text, BodyAlt, Body} from 'sail/Text';
import ChartIcon from 'sail/ChartIcon';
import type {IconColor} from 'sail/Icon/types';
import Button from 'sail/Button';
import {defineMessages, FormattedMessage} from 'react-intl';
import {Card, CardHeader, CardBadge, CardBody} from './framework';
import type {CardBadgeDefinition} from './framework';
import './IntegrationCard.scss';

type CardTextItem = {|label: string, text: string|};

type CardText = string | Array<CardTextItem>;

type IntegrationCardProps = {|
  badge?: CardBadgeDefinition,
  text: CardText,
  title: string,
  href: string,
  image: string,
  effort?: number,
  effortColor?: IconColor,
  buttonLabel?: string,
  buttonHref?: string,
  titleBadge?: boolean,
|};

type EffortCaptionType = {|effort: number, effortColor: null | IconColor|};

type IntegrationCardTextType = {|text: CardText|};

type CardButtonType = {|label: string, href: string|};

const messages = defineMessages({
  effort: {
    id: 'docsApp.components.IntegrationCard.effort',
    description: 'Effort',
    defaultMessage: 'Effort',
  },
});

const maxEffort = 5;

const getEffortColor = (effort: number) => {
  switch (effort) {
    case 1:
    case 2:
      return 'green300';
    case 3:
    case 4:
    case 5:
      return 'yellow';
    default:
      return 'gray300';
  }
};

const EffortCaption = ({effort, effortColor}: EffortCaptionType) => {
  const color = effortColor || getEffortColor(effort);

  const effortIndicator = [];
  for (let i = 0; i < maxEffort; i++) {
    effortIndicator.push(
      <ChartIcon
        key={i}
        icon="circle"
        iconStyle="solid"
        color={i < effort ? color : 'gray300'}
        size={12}
      />,
    );
  }

  return (
    <Box flex style={{marginTop: 'auto'}}>
      <Group
        direction="horizontal"
        alignItems="center"
        spacing={8}
        padding={{top: 12}}
      >
        <Text weight="medium" size={13}>
          <FormattedMessage {...messages.effort} />:
        </Text>
        <Group spacing={2} direction="horizontal">
          {effortIndicator}
        </Group>
      </Group>
    </Box>
  );
};

const IntegrationCardText = ({text}: IntegrationCardTextType) => {
  if (Array.isArray(text)) {
    const formattedText = [];
    text.forEach((item) =>
      formattedText.push(
        <React.Fragment key={item.text}>
          <BodyAlt className="Card-text-label">{item.label}</BodyAlt>
          <Body>{item.text}</Body>
        </React.Fragment>,
      ),
    );
    return <Group spacing={4}>{formattedText}</Group>;
  } else {
    return text;
  }
};

const CardButton = ({label, href}: CardButtonType) => (
  <Box flex margin={{top: 20, left: 2}}>
    <Button label={label} color="white" href={href} />
  </Box>
);

export const IntegrationCard = ({
  badge,
  text,
  title,
  href,
  image,
  effort,
  effortColor,
  buttonLabel,
  buttonHref,
  titleBadge,
}: IntegrationCardProps) => (
  <Card className="IntegrationCard">
    <Link href={href} title={title}>
      <Box className="Card-image">
        {badge && !titleBadge && <CardBadge badge={badge} />}
        <img alt="" src={image} />
      </Box>
      {titleBadge ? (
        <CardHeader text={title} badge={badge} />
      ) : (
        <CardHeader text={title} />
      )}
    </Link>
    <CardBody>
      <IntegrationCardText text={text} />
    </CardBody>
    {effort && (
      <EffortCaption effort={effort} effortColor={effortColor || null} />
    )}
    {buttonLabel && (
      <CardButton label={buttonLabel} href={buttonHref || href} />
    )}
  </Card>
);
